import { inject, Injectable } from '@angular/core';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import {
	GetDocumentResponse,
	GetTokenResponse,
	ProfileIdUserMapping,
	SetProfileIdMappingRequest,
	SubmitFileResponse,
	UpdateProfileIdMappingsRequest,
} from '@fnModels/stewart';
import { fnCallable } from '@fnModels/stewart/fnCallable';
import { SubmitFile } from '@models/title-insurance/stewart';
import { environment } from 'src/environments/environment';
import { Logger } from '@core/logger';

@Injectable({ providedIn: 'root' })
export class StewartFunctions {
	private clientId = environment.stewartOAuth.stewartClientId;
	private fns = inject(AngularFireFunctions);

	setProfileId = (userId: string, profileId: string) =>
		this.fns.httpsCallable<SetProfileIdMappingRequest, void>(
			fnCallable.setProfileId_v2,
		)({ mapping: { userId, profileId } });

	setProfileIds = (mappings: ProfileIdUserMapping[]) =>
		this.fns.httpsCallable<UpdateProfileIdMappingsRequest, void>(
			fnCallable.setProfileIds_v2,
		)({ mappings });

	storeAuth_v2 = (accessToken: string, refreshToken: string) =>
		this.doCallableWithObservableClientId_v2<void>(
			fnCallable.storeAuth_v2,
			(clientId) => ({
				accessToken,
				refreshToken,
				clientId,
			}),
		);

	refreshToken_v2 = () =>
		this.doCallableWithObservableClientId_v2<void>(
			fnCallable.refreshToken_v2,
			(clientId) => ({ clientId }),
		);

	submitFile_v2 = (
		matterId: string,
		file: SubmitFile,
		assignedLawyerId: string,
	) =>
		this.doCallableWithObservableClientId_v2<SubmitFileResponse>(
			fnCallable.submitFile_v2,
			(clientId) => ({
				matterId,
				file,
				clientId,
				assignedLawyerId,
			}),
		);

	getStatus_v2 = (
		matterId: string,
		fileNumber: string | number,
		submittedPayload: SubmitFile,
	) =>
		this.doCallableWithObservableClientId_v2<void>(
			fnCallable.getFileStatus_v2,
			(clientId) => ({
				matterId,
				fileNumber,
				clientId,
				submittedPayload,
			}),
		);

	connected_v2 = () =>
		this.doCallableWithObservableClientId_v2<boolean>(
			fnCallable.connected_v2,
			(clientId) => ({ clientId }),
		);

	getToken_v2 = (userId: string) =>
		this.doCallableWithObservableClientId_v2<GetTokenResponse>(
			fnCallable.getToken_v2,
			(clientId) => ({ clientId, userId }),
		);

	getDocument_v2 = (documentUrl: string) =>
		this.doCallableWithObservableClientId_v2<GetDocumentResponse>(
			fnCallable.getDocument_v2,
			(clientId) => ({ clientId, documentUrl }),
		);

	doCallableWithObservableClientId_v2 = <R>(
		name: string,
		getBody: (clientId: string) => Record<string, unknown>,
	) => {
		Logger.info(
			`${StewartFunctions.name} - invoking function of type: ${name}`,
		);

		return this.fns.httpsCallable<ReturnType<typeof getBody>, R>(name)(
			getBody(this.clientId),
		);
	};
}
